import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

interface HeroProps {
  onConnectClick?: () => void;
  onWhoWeAreClick?: (e: React.MouseEvent) => void;
}


const logos = ["AI", "AUTOMATION", "CLOUD", "CHATBOT", "PAAS", "AZURE", "AWS", "GCP", "AGENTIC"];

const MarqueeGroup = ({ logos }: { logos: string[] }) => (
  <motion.div 
    className="flex flex-shrink-0 items-center gap-16 md:gap-32 pr-16 md:pr-32"
    animate={{ x: "-100%" }}
    transition={{ 
      repeat: Infinity, 
      ease: "linear", 
      duration: 50
    }}
  >
    {logos.map((logo, i) => (
      <div key={i} className="flex items-center gap-6 md:gap-8">
         {i % 2 !== 0 && (
             <span className="text-2xl md:text-3xl font-serif italic text-gray-500 dark:text-gray-600"></span>
         )}
         <span className={`text-xl md:text-3xl font-bold tracking-[0.15em] uppercase ${i % 2 !== 0 ? 'font-serif italic text-gray-500 dark:text-gray-400' : 'font-mono text-gray-800 dark:text-gray-300'}`}>
            {logo}
         </span>
      </div>
    ))}
  </motion.div>
);

const Hero: React.FC<HeroProps> = ({ onConnectClick, onWhoWeAreClick }) => {
  return (
    <section className="relative pt-32 pb-10 lg:pt-48 lg:pb-16 flex flex-col items-center justify-start text-center px-4 overflow-hidden min-h-[80vh]">
      
      {/* Integrated Background Image */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-full pointer-events-none z-0">
          <motion.div 
             initial={{ opacity: 0 }}
             animate={{ opacity: 0.8104 }}
             transition={{ duration: 1.2 }}
             style={{
                 transform: "translateY(5.688px)",
                 willChange: "transform"
             }}
             className="w-full flex justify-center"
          >
             <div className="relative w-full h-96 flex justify-center items-end flex">
                <div className="absolute w-[120vw] md:w-[900px] h-[600px] rounded-[100%] border-t-[100px] border-red-200 dark:border-red-900/40 blur-[100px] translate-y-[50%] left-1/2 -translate-x-1/2"></div>
                <div className="absolute w-[100vw] md:w-[800px] h-[500px] rounded-[100%] border-t-[60px] border-red-500 dark:border-red-600 blur-[50px] translate-y-[50%] opacity-90 left-1/2 -translate-x-1/2"></div>
                <div className="absolute w-[95vw] md:w-[780px] h-[480px] rounded-[100%] border-t-[30px] border-rose-400 dark:border-rose-500 blur-[25px] translate-y-[50%] left-1/2 -translate-x-1/2"></div>
                <motion.div 
                    animate={{ opacity: [0.8, 1, 0.8] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute w-[90vw] md:w-[760px] h-[460px] rounded-[100%] border-t-[8px] border-red-300 dark:border-red-200 blur-[10px] translate-y-[50%] left-1/2 -translate-x-1/2"
                ></motion.div>
             </div>
          </motion.div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col items-center">
        
        {/* Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-[#1a0505] border border-gray-200 dark:border-brand-red/20 mb-8 shadow-[0_0_15px_rgba(220,38,38,0.1)]"
        >
          <div className="w-4 h-4 rounded-full bg-brand-red flex items-center justify-center">
            <span className="text-[8px] font-bold text-white dark:text-black">AI</span>
          </div>
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 tracking-wide">AI Agent Solutions</span>
        </motion.div>

        {/* Main Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-medium leading-[1.1] tracking-tight mb-6 text-gray-900 dark:text-white max-w-4xl mx-auto">
          AI-Driven Success <br className="hidden md:block" />
          Redefining the Future.
        </h1>

        {/* Subheadline */}
        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-base sm:text-lg text-gray-600 dark:text-gray-100 max-w-2xl mb-10 leading-relaxed font-light px-2"
        >
          Creating innovative AI agent solutions that redefine the future of intelligent automation.
        </motion.p>

        {/* Buttons */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center gap-4 mb-24 relative z-20"
        >
          <button 
            onClick={onConnectClick}
            className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-black text-sm font-semibold rounded-full hover:bg-gray-700 dark:hover:bg-gray-100 transition-all flex items-center gap-2 group shadow-xl"
          >
            Connect With Us
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          
          <button 
            onClick={onWhoWeAreClick}
            className="px-6 py-3 bg-white/50 dark:bg-[#1a1a1a]/50 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-white text-sm font-medium rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a]/50 transition-all backdrop-blur-md"
          >
            Who is Neotronics?
          </button>
        </motion.div>

        {/* Marquee Logo Strip */}
        <div className="relative w-full overflow-hidden opacity-40 hover:opacity-80 transition-opacity duration-500">
            <div className="absolute inset-y-0 left-0 w-20 bg-transparent z-20 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-20bg-transparent z-20 pointer-events-none" />
              
            <div className="flex select-none">
              <MarqueeGroup logos={logos} />
              <MarqueeGroup logos={logos} />
              <MarqueeGroup logos={logos} /> 
            </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;